import React, { useState, useEffect, useMemo } from 'react';
import { Bug, ArrowLeft, Trash2, MessageSquare, Clock, User, Monitor, MapPin, Camera, Filter, CheckCircle, AlertTriangle, XCircle, RefreshCw, Copy, Mail, Send, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: RefreshCw },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
  wont_fix: { label: "Won't Fix", color: 'bg-stone-100 text-stone-600 border-stone-200', icon: XCircle },
  duplicate: { label: 'Duplicate', color: 'bg-purple-100 text-purple-600 border-purple-200', icon: XCircle },
};

const PRIORITY_COLORS = {
  Critical: 'bg-red-600 text-white',
  High: 'bg-orange-500 text-white',
  Medium: 'bg-amber-500 text-white',
  Low: 'bg-blue-500 text-white',
};

export default function AdminBugReports() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [saving, setSaving] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  useEffect(() => { fetchReports(); }, []);

  const fetchReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/bug-reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(res.data.reports || []);
    } catch {
      toast.error('Failed to load bug reports');
    } finally {
      setLoading(false);
    }
  };

  const filteredReports = useMemo(() => {
    if (statusFilter === 'all') return reports;
    return reports.filter(r => r.status === statusFilter);
  }, [reports, statusFilter]);

  const statusCounts = useMemo(() => {
    const counts = { all: reports.length, new: 0, in_progress: 0, resolved: 0, wont_fix: 0, duplicate: 0 };
    reports.forEach(r => { if (counts[r.status] !== undefined) counts[r.status]++; });
    return counts;
  }, [reports]);

  const updateStatus = async (reportId, newStatus) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/bug-reports/${reportId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(prev => prev.map(r => r.id === reportId ? { ...r, status: newStatus } : r));
      if (selectedReport?.id === reportId) {
        setSelectedReport(prev => ({ ...prev, status: newStatus }));
      }
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
    } catch {
      toast.error('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const saveNotes = async (reportId) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/bug-reports/${reportId}`, { admin_notes: adminNotes }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(prev => prev.map(r => r.id === reportId ? { ...r, admin_notes: adminNotes } : r));
      toast.success('Notes saved');
    } catch {
      toast.error('Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  const deleteReport = async (reportId) => {
    if (!window.confirm('Delete this bug report permanently?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/bug-reports/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(prev => prev.filter(r => r.id !== reportId));
      if (selectedReport?.id === reportId) setSelectedReport(null);
      toast.success('Bug report deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const openReport = (report) => {
    setSelectedReport(report);
    setAdminNotes(report.admin_notes || '');
    setShowReplyForm(false);
    setReplyMessage('');
    // Mark as read if new
    if (report.status === 'new') {
      updateStatus(report.id, 'in_progress');
    }
  };

  const copyForDev = (report) => {
    const browserParts = (report.browser_info || '').split('|').map(s => s.trim());
    const text = [
      `## Bug Report: ${report.bug_type}`,
      report.feature_area ? `**Feature Area:** ${report.feature_area}` : null,
      `**Priority:** ${report.priority}`,
      `**Status:** ${STATUS_CONFIG[report.status]?.label || report.status}`,
      `**Reported by:** ${report.user_name} (${report.user_email || 'no email'})`,
      `**Date:** ${format(new Date(report.created_at), 'PPp')}`,
      `**Page:** ${report.page || 'unknown'}`,
      `**Browser:** ${browserParts[0] || 'unknown'}`,
      browserParts[1] ? `**Screen:** ${browserParts[1]}` : null,
      browserParts[2] ? `**Viewport:** ${browserParts[2]}` : null,
      report.ai_summary ? `\n**AI Analysis:** ${report.ai_summary}` : null,
      '',
      `### Description`,
      report.description,
      report.steps_to_reproduce ? `\n### Steps to Reproduce\n${report.steps_to_reproduce}` : null,
      report.screenshot ? `\n### Screenshot\nAttached (storage path: ${report.screenshot})` : null,
      report.admin_notes ? `\n### Admin Notes\n${report.admin_notes}` : null,
    ].filter(Boolean).join('\n');
    
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Bug report copied to clipboard');
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  const sendReply = async (reportId) => {
    if (!replyMessage.trim()) {
      toast.error('Please write a message');
      return;
    }
    setSendingReply(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/bug-reports/${reportId}/reply`, { message: replyMessage }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Reply sent to user via email');
      setShowReplyForm(false);
      setReplyMessage('');
      // Refresh to show reply in history
      fetchReports();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to send reply';
      toast.error(detail);
    } finally {
      setSendingReply(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center honeycomb-bg">
        <div className="animate-spin h-8 w-8 border-4 border-red-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="pb-8">
      {/* Header */}
      <div className="px-6 py-4">
        <div className="max-w-7xl">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
                <Bug className="h-6 w-6 text-amber-600" />
                Bug Reports
              </h1>
              <p className="text-sm text-stone-500 mt-1">{reports.length} total reports | {statusCounts.new} new</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl px-4 mt-6">
        {/* Status Filter Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap" data-testid="bug-status-filters">
          {[
            { key: 'all', label: 'All' },
            { key: 'new', label: 'New' },
            { key: 'in_progress', label: 'In Progress' },
            { key: 'resolved', label: 'Resolved' },
            { key: 'wont_fix', label: "Won't Fix" },
            { key: 'duplicate', label: 'Duplicate' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                statusFilter === tab.key
                  ? 'bg-red-600 text-white shadow-md'
                  : 'bg-white text-stone-600 hover:bg-stone-100 border border-stone-200'
              }`}
              data-testid={`filter-${tab.key}`}
            >
              {tab.label}
              {statusCounts[tab.key] > 0 && (
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${
                  statusFilter === tab.key ? 'bg-white/20' : 'bg-stone-200'
                }`}>
                  {statusCounts[tab.key]}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Reports List */}
          <div className="lg:col-span-1 space-y-3">
            {filteredReports.length === 0 ? (
              <div className="bg-white rounded-xl border border-stone-200 p-8 text-center text-stone-500">
                No {statusFilter !== 'all' ? STATUS_CONFIG[statusFilter]?.label.toLowerCase() : ''} bug reports
              </div>
            ) : (
              filteredReports.map(report => {
                const statusConf = STATUS_CONFIG[report.status] || STATUS_CONFIG.new;
                return (
                  <button
                    key={report.id}
                    onClick={() => openReport(report)}
                    className={`w-full text-left bg-white rounded-xl border p-4 hover:shadow-md transition-all ${
                      selectedReport?.id === report.id ? 'border-red-400 ring-2 ring-red-100' : 'border-stone-200'
                    } ${report.status === 'new' ? 'border-l-4 border-l-red-500' : ''}`}
                    data-testid={`bug-report-${report.id}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${PRIORITY_COLORS[report.priority] || PRIORITY_COLORS.Medium}`}>
                        {report.priority}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${statusConf.color}`}>
                        {statusConf.label}
                      </span>
                    </div>
                    <p className="font-medium text-stone-800 text-sm line-clamp-2">{report.description}</p>
                    {report.feature_area && (
                      <span className="inline-block mt-1.5 text-xs bg-indigo-50 text-indigo-600 border border-indigo-200 px-2 py-0.5 rounded-full">
                        {report.feature_area}
                      </span>
                    )}
                    <div className="mt-2 flex items-center gap-3 text-xs text-stone-400">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" /> {report.user_name}
                      </span>
                      <span>{report.bug_type}</span>
                      <span>{format(new Date(report.created_at), 'MMM d')}</span>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-2">
            {selectedReport ? (
              <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden" data-testid="bug-report-detail">
                {/* Detail Header */}
                <div className="border-b border-stone-100 p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold px-3 py-1 rounded-full ${PRIORITY_COLORS[selectedReport.priority]}`}>
                        {selectedReport.priority}
                      </span>
                      <span className="text-sm text-stone-500">{selectedReport.bug_type}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => copyForDev(selectedReport)}
                        className="text-stone-400 hover:text-blue-600 p-1.5 rounded-lg hover:bg-blue-50 transition-colors"
                        title="Copy for dev"
                        data-testid="copy-bug-report"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setShowReplyForm(!showReplyForm)}
                        className={`p-1.5 rounded-lg transition-colors ${showReplyForm ? 'text-amber-600 bg-amber-50' : 'text-stone-400 hover:text-amber-600 hover:bg-amber-50'}`}
                        title="Reply to user"
                        data-testid="reply-to-user-btn"
                      >
                        <Mail className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => deleteReport(selectedReport.id)}
                        className="text-stone-400 hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                        title="Delete report"
                        data-testid="delete-bug-report"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Status Actions */}
                  <div className="flex gap-2 flex-wrap">
                    {Object.entries(STATUS_CONFIG).map(([key, conf]) => {
                      const Icon = conf.icon;
                      return (
                        <button
                          key={key}
                          onClick={() => updateStatus(selectedReport.id, key)}
                          disabled={saving}
                          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                            selectedReport.status === key
                              ? conf.color + ' ring-2 ring-offset-1'
                              : 'bg-stone-50 text-stone-500 border-stone-200 hover:bg-stone-100'
                          }`}
                          data-testid={`status-${key}`}
                        >
                          <Icon className="h-3 w-3" />
                          {conf.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Report Content */}
                <div className="p-5 space-y-5">
                  {/* Metadata */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div className="bg-stone-50 rounded-lg p-3">
                      <div className="text-stone-400 text-xs mb-1 flex items-center gap-1"><User className="h-3 w-3" /> Reported by</div>
                      <div className="font-medium text-stone-800">{selectedReport.user_name}</div>
                      {selectedReport.user_email && <div className="text-xs text-stone-500">{selectedReport.user_email}</div>}
                    </div>
                    <div className="bg-stone-50 rounded-lg p-3">
                      <div className="text-stone-400 text-xs mb-1 flex items-center gap-1"><Clock className="h-3 w-3" /> Date</div>
                      <div className="font-medium text-stone-800">{format(new Date(selectedReport.created_at), 'PPp')}</div>
                    </div>
                    <div className="bg-stone-50 rounded-lg p-3">
                      <div className="text-stone-400 text-xs mb-1 flex items-center gap-1"><MapPin className="h-3 w-3" /> Page</div>
                      <div className="font-medium text-stone-800 font-mono text-xs break-all">{selectedReport.page || '—'}</div>
                    </div>
                    <div className="bg-stone-50 rounded-lg p-3">
                      <div className="text-stone-400 text-xs mb-1 flex items-center gap-1"><Monitor className="h-3 w-3" /> Browser</div>
                      <div className="font-medium text-stone-800 text-xs break-all line-clamp-2">{selectedReport.browser_info?.split('|')[0] || '—'}</div>
                    </div>
                  </div>

                  {/* AI Categorization */}
                  {(selectedReport.feature_area || selectedReport.ai_summary) && (
                    <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4" data-testid="ai-categorization">
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="h-4 w-4 text-indigo-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
                        <span className="text-xs font-bold text-indigo-700 uppercase tracking-wide">AI Analysis</span>
                      </div>
                      {selectedReport.feature_area && (
                        <div className="mb-2">
                          <span className="text-xs text-indigo-500">Feature Area:</span>
                          <span className="ml-2 text-sm font-semibold text-indigo-800 bg-indigo-100 px-2 py-0.5 rounded-full">{selectedReport.feature_area}</span>
                        </div>
                      )}
                      {selectedReport.ai_summary && (
                        <p className="text-sm text-indigo-700">{selectedReport.ai_summary}</p>
                      )}
                    </div>
                  )}

                  {/* Description */}
                  <div>
                    <h3 className="font-bold text-stone-800 mb-2">Description</h3>
                    <div className="bg-stone-50 rounded-xl p-4 text-stone-700 text-sm whitespace-pre-wrap">
                      {selectedReport.description}
                    </div>
                  </div>

                  {/* Steps */}
                  {selectedReport.steps_to_reproduce && (
                    <div>
                      <h3 className="font-bold text-stone-800 mb-2">Steps to Reproduce</h3>
                      <div className="bg-stone-50 rounded-xl p-4 text-stone-700 text-sm whitespace-pre-wrap">
                        {selectedReport.steps_to_reproduce}
                      </div>
                    </div>
                  )}

                  {/* Screenshot */}
                  {selectedReport.screenshot && (
                    <div>
                      <h3 className="font-bold text-stone-800 mb-2 flex items-center gap-2"><Camera className="h-4 w-4" /> Screenshot</h3>
                      <ScreenshotImage path={selectedReport.screenshot} />
                    </div>
                  )}

                  {/* Reply to User Form */}
                  {showReplyForm && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="reply-form">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-bold text-amber-800 flex items-center gap-2">
                          <Mail className="h-4 w-4" /> Reply to {selectedReport.user_name}
                        </h3>
                        <button onClick={() => setShowReplyForm(false)} className="text-amber-400 hover:text-amber-600">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      {selectedReport.user_email ? (
                        <>
                          <p className="text-xs text-amber-600 mb-2">
                            Email will be sent to: <span className="font-medium">{selectedReport.user_email}</span>
                          </p>
                          <Textarea
                            value={replyMessage}
                            onChange={(e) => setReplyMessage(e.target.value)}
                            placeholder="Hi — thanks for reporting this. Here's what we found..."
                            className="min-h-[80px] text-sm bg-white"
                            data-testid="reply-message"
                          />
                          <Button
                            onClick={() => sendReply(selectedReport.id)}
                            disabled={sendingReply}
                            className="mt-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg"
                            data-testid="send-reply-btn"
                          >
                            {sendingReply ? (
                              <div className="flex items-center gap-2">
                                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                                Sending...
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <Send className="h-4 w-4" /> Send Email Reply
                              </div>
                            )}
                          </Button>
                        </>
                      ) : (
                        <p className="text-sm text-amber-700">This user has no email address on file — unable to send reply.</p>
                      )}
                    </div>
                  )}

                  {/* Previous Replies */}
                  {selectedReport.replies && selectedReport.replies.length > 0 && (
                    <div>
                      <h3 className="font-bold text-stone-800 mb-2 flex items-center gap-2">
                        <Mail className="h-4 w-4" /> Reply History ({selectedReport.replies.length})
                      </h3>
                      <div className="space-y-2">
                        {selectedReport.replies.map((reply, idx) => (
                          <div key={idx} className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium text-amber-700">{reply.admin_name || 'Admin'}</span>
                              <span className="text-xs text-amber-500">{reply.sent_at ? format(new Date(reply.sent_at), 'PPp') : ''}</span>
                            </div>
                            <p className="text-sm text-stone-700 whitespace-pre-wrap">{reply.message}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Admin Notes */}
                  <div>
                    <h3 className="font-bold text-stone-800 mb-2 flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" /> Admin Notes
                    </h3>
                    <Textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="Add your investigation notes, resolution details..."
                      className="min-h-[100px] text-sm"
                      data-testid="admin-notes"
                    />
                    <Button
                      onClick={() => saveNotes(selectedReport.id)}
                      disabled={saving}
                      className="mt-2 bg-stone-800 hover:bg-stone-900 text-white rounded-lg"
                      data-testid="save-notes-btn"
                    >
                      {saving ? 'Saving...' : 'Save Notes'}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-stone-200 p-12 text-center">
                <Bug className="h-12 w-12 text-stone-300 mx-auto mb-3" />
                <p className="text-stone-500 font-medium">Select a bug report to view details</p>
                <p className="text-stone-400 text-sm mt-1">Click on any report from the list</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Screenshot component with blob URL fetching (same pattern as InspectionView)
const ScreenshotImage = ({ path }) => {
  const [blobUrl, setBlobUrl] = React.useState(null);
  
  React.useEffect(() => {
    let cancelled = false;
    const fetchImage = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${BACKEND_URL}/api/files/${path}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        });
        if (!cancelled) setBlobUrl(URL.createObjectURL(res.data));
      } catch { /* ignore load error */ }
    };
    if (path && path.includes('/')) fetchImage();
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [path]);
  
  if (!blobUrl) return <div className="w-full h-48 bg-stone-100 rounded-xl animate-pulse" />;
  return <img src={blobUrl} alt="Bug screenshot" className="w-full rounded-xl border border-stone-200 max-h-96 object-contain bg-stone-50" />;
};
